<template>
  <div>
    <div v-if="isLoggedIn && hasRoomInAccount">
      <div class="flex gap-2 py-2 px-4 border border-info/30 bg-info/5 mb-5">Uploading to Pacsbin user account: <UserSession></UserSession></div>
      <template v-if="remoteCaseUrl">
        Case created!
        <a :href="remoteCaseUrl">{{ remoteCaseUrl }}</a>
      </template>
      <template v-if="!remoteCaseUrl">
        <div class="mb-3">
          <label>Case description</label><br />
          <input
            type="text"
            class="w-full input input-bordered"
            v-model="caseDef.metadata!.description"
          />
        </div>
        <div class="mb-3">
          <label>Case notes</label><br />
          <textarea
            class="h-20 w-full textarea textarea-bordered"
            v-model="caseDef.metadata!.notes"
          ></textarea>
        </div>
        <div class="mb-3">
          <label class="">Add to collection</label>
          <PlaylistChooser
            v-model="caseDef.metadata!.playlists"
            :user-id="userSession!._id"
            :playlist-list-url="playlistListUrl"
          />
        </div>
        <div class="mb-3">
          <label class="">Add tags</label>
          <TagChooser
            v-model="caseDef.metadata!.tags"
            :user-id="userSession!._id"
            :tag-list-url="tagListUrl"
          />
        </div>
        <div class="diagnosis-editor">
          <label class="">Add diagnosis</label>
          <DiagnosisEditor
            editable
            v-model="caseDef.metadata!.diagnosis"
          ></DiagnosisEditor>
        </div>
        <button
          class="btn btn-success mt-10"
          :class="{ 'opacity-50': uploadInProgress }"
          @click="finish"
          :disabled="uploadInProgress"
        >
          {{ uploadInProgress ? "Uploading..." : "Start upload" }}
        </button>
      </template>
      <template v-if="exportError">
        There was an error exporting the studies, please check the logs.
      </template>
    </div>
    <div v-else>
      <div v-if="!isLoggedIn">You must log in to upload cases.</div>
      <div v-if="isLoggedIn && !hasRoomInAccount">
        You have no remaining space in your account.
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import UploadCaseDef from "../../types/UploadCaseDef";
import processAndUpload from "./process-and-upload";
import UserSession from "../../UserSession.vue";
import { isLoggedIn, userSession, hasRoomInAccount } from "../../user-session";
import DiagnosisEditor from "../../components/diagnosis-chooser/DiagnosisEditor.vue";
import TagChooser from "../../components/TagChooser.vue";
import PlaylistChooser from "../../components/PlaylistChooser.vue";
import { ref } from "vue";
import { addLog } from "../../logger";
import getRemoteURL from "../../get-remote-url";

const props = defineProps<{ caseDef: UploadCaseDef }>();
const remoteCaseUrl = ref<string | null>(null);
const exportError = ref<string | null>(null);
const uploadInProgress = ref(false);
const tagListUrl = ref(getRemoteURL("api/tag/list"));
const playlistListUrl = ref(getRemoteURL("api/playlist/list"));

props.caseDef.metadata = {
  description: props.caseDef.studies[0]?.studyDescription || "New case",
  notes: "",
  playlists: [],
  tags: [],
  diagnosis: [],
};

props.caseDef.studies.forEach((study) => {
  study.metadata = {
    description: study.studyDescription || `${study.series[0].modality} study`,
  };
});

async function finish() {
  if (uploadInProgress.value) {
    return;
  }
  if (!userSession.value?._id) {
    throw new Error("Must be logged in to upload.");
  }
  uploadInProgress.value = true;
  if (import.meta.env.MODE !== "development") {
    try {
      remoteCaseUrl.value = await processAndUpload(
        props.caseDef
        // userSession.value._id
      );
    } catch (err) {
      exportError.value = "Error processing export";
      addLog("error", (err as Error).toString());
      console.error(err);
    }
  } else {
    exportError.value = "Aborted upload in dev mode";
    addLog("error", "Aborted upload in dev mode");
  }
}
</script>
